import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useCallback, useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { queuesApi } from 'apis/queues';
import { appActions } from 'app/app.actions';
import { HookFormQueuesSelect } from 'components/hookForm/hookFormQueuesSelect';
/* eslint-disable no-restricted-imports */
import { transferToAgentActions } from 'components/shared/transferToAgentModal/actions/transferToAgentModal';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './conversationQueueTransferModal.module.scss';

type ConversationQueueTransferModalFormValues = {
  queueId: string[];
};

const formId = 'conversation-queue-transfer-form';

/** A component for transferring a conversation to a different queue. */
export const ConversationQueueTransferModal = createAppModal(
  { type: 'app:conversationQueueTransfer' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const containerRef = useRef<HTMLFormElement>(null);
    const csid = modal.arg.csid;
    const showWarmTransferOption = !!modal.arg.isWarmTransferAvailable;

    const form = useForm<ConversationQueueTransferModalFormValues>({
      defaultValues: { queueId: [] },
    });

    const queueId = form.watch('queueId')?.[0];

    /** Handles closing the transfer to queue modal. */
    const handleClose = useCallback(() => {
      ampli.modalTransferToQueueCancelClicked();
      form.reset();
      hide();
    }, [hide, form]);

    useEffect(() => {
      if (isOpen) {
        const timer = setTimeout(() => {
          const button = containerRef.current?.querySelector('button');
          button?.click();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isOpen]);

    /** Handles direct transfer to queue (blind transfer). */
    const handleTransferNow: SubmitHandler<ConversationQueueTransferModalFormValues> =
      useCallback(
        async (values) => {
          const queueId = values.queueId[0];
          if (!csid || !queueId) return;

          const { data: queue } = await dispatch(
            queuesApi.endpoints.readQueue.initiate({ id: queueId }),
          ).unwrap();

          if (!queue) return;

          ampli.modalTransferToQueueConfirmClicked({
            csid,
            queueId: queue.id,
          });

          dispatch(
            appActions.conversationTransferRequest.pending({
              request: {
                type: 'internal_queue',
                csid,
                queue,
              },
            }),
          );

          handleClose();
        },
        [csid, dispatch, handleClose],
      );

    /** Handles opening the agent selection modal for warm transfer. */
    const handleCallAgentFirst = useCallback(() => {
      if (!csid || !queueId) return;

      ampli.modalTransferToQueueConfirmClicked({
        csid,
        queueId,
      });

      handleClose();

      dispatch(
        transferToAgentActions.transferToAgentModalOpenByCsid({
          csid,
          queueId,
        }),
      );
    }, [csid, dispatch, handleClose, queueId]);

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead
          title={`Transfer #${csid} to a different queue`}
          onClose={handleClose}
        />

        <ModalBody>
          <form
            id={formId}
            ref={containerRef}
            onSubmit={form.handleSubmit(handleTransferNow)}
          >
            <HookFormQueuesSelect
              name="queueId"
              control={form.control}
              rules={{
                required: true,
              }}
              noAlert
              noLabel
            />
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleClose}
          confirmName={showWarmTransferOption ? 'Transfer now' : 'Transfer'}
          confirmProps={{
            type: 'submit',
            form: formId,
            id: 'transfer-queue-confirm-button',
            isLoading: form.formState.isSubmitting,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
          }}
          confirmOnClick={noop}
          confirmSecondaryOnClick={
            showWarmTransferOption ? handleCallAgentFirst : undefined
          }
          confirmSecondaryName={
            showWarmTransferOption ? 'Call agent first' : undefined
          }
          confirmSecondaryProps={
            showWarmTransferOption ? { disabled: !queueId } : undefined
          }
        />
      </Modal>
    );
  },
);
